
import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Faq } from "@/types/faq-types";
import { useToast } from "@/components/ui/use-toast";
import DOMPurify from "isomorphic-dompurify";

export const useFaqs = () => {
  const [faqs, setFaqs] = useState<Faq[]>([]);
  const [filteredFaqs, setFilteredFaqs] = useState<Faq[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  
  // Load FAQs from database
  const loadFaqs = useCallback(async () => {
    setLoading(true);
    try {
      console.log("Loading FAQs for public display...");
      const { data, error } = await supabase
        .from('faqs')
        .select('*')
        .eq('status', 'published')
        .eq('active', true)
        .order('display_order', { ascending: true });

      if (error) {
        throw error;
      }

      console.log(`Loaded ${data?.length || 0} published FAQs`);
      setFaqs(data || []);
      setFilteredFaqs(data || []);
    } catch (error: any) {
      console.error("Error loading FAQs:", error);
      toast({
        variant: "destructive",
        title: "Error loading FAQs",
        description: "Unable to load FAQs at this time. Please try again later."
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  // Strip HTML tags from text for better search
  const stripHtml = (html: string): string => {
    // Create a DOM element from the HTML content
    const tempElement = document.createElement('div');
    tempElement.innerHTML = DOMPurify.sanitize(html);
    // Return the text content without HTML tags
    return tempElement.textContent || tempElement.innerText || '';
  };

  // Normalize text for comparison (lowercase, remove extra spaces, etc.)
  const normalizeText = (text: string): string => {
    return text
      .toLowerCase()
      .replace(/[^\w\s]/g, ' ') // Replace non-alphanumeric with spaces
      .replace(/\s+/g, ' ')     // Replace multiple spaces with single space
      .trim();
  };

  // Improved search function with better matching and content normalization
  const searchFaqs = useCallback((query: string) => {
    setSearchQuery(query);
    
    if (!query.trim()) {
      setFilteredFaqs(faqs);
      return;
    }
    
    const normalizedQuery = normalizeText(query);
    const queryWords = normalizedQuery.split(/\s+/).filter(word => word.length > 1);
    
    if (queryWords.length === 0) {
      setFilteredFaqs(faqs);
      return;
    }
    
    // Score-based matching with enhanced text processing
    const results = faqs
      .map(faq => {
        // Normalize and prepare content for search
        const normalizedQuestion = normalizeText(faq.question);
        const normalizedAnswer = normalizeText(stripHtml(faq.answer));
        
        let score = 0;
        let matchedTerms: string[] = [];
        
        // Check for exact query matches (highest priority)
        if (normalizedQuestion.includes(normalizedQuery)) {
          score += 100;
          matchedTerms.push(normalizedQuery);
        }
        
        if (normalizedAnswer.includes(normalizedQuery)) {
          score += 50;
          matchedTerms.push(normalizedQuery);
        }
        
        // Word boundary matches (higher priority than substring)
        queryWords.forEach(word => {
          // Word boundary regex to match whole words
          const wordBoundaryRegex = new RegExp(`\\b${word}\\b`, 'i');
          
          if (wordBoundaryRegex.test(normalizedQuestion)) {
            score += 25;
            matchedTerms.push(word);
          }
          
          if (wordBoundaryRegex.test(normalizedAnswer)) {
            score += 15;
            matchedTerms.push(word);
          }
          
          // Partial matches with word beginnings (lower priority)
          if (normalizedQuestion.includes(word)) {
            score += 10;
            matchedTerms.push(word);
          }
          
          if (normalizedAnswer.includes(word)) {
            score += 5;
            matchedTerms.push(word);
          }
          
          // Bonus for word at the beginning of question
          if (normalizedQuestion.startsWith(word)) {
            score += 15;
          }
        });
        
        // Remove duplicate matched terms
        matchedTerms = [...new Set(matchedTerms)];
        
        return { faq, score, matchedTerms };
      })
      .filter(item => item.score > 0) // Only keep items with matches
      .sort((a, b) => b.score - a.score) // Sort by score, highest first
      .map(item => ({
        ...item.faq,
        matchedTerms: item.matchedTerms // Attach matched terms for highlighting
      }));
    
    console.log(`Search for "${query}" returned ${results.length} results`);
    setFilteredFaqs(results);
  }, [faqs]);

  // Load FAQs on component mount
  useEffect(() => {
    loadFaqs();
  }, [loadFaqs]);

  return {
    faqs: filteredFaqs,
    loading,
    searchQuery,
    searchFaqs,
    refreshFaqs: loadFaqs,
    hasResults: filteredFaqs.length > 0,
    totalCount: faqs.length
  };
};
